import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', (event) => {
      const path = event.composedPath();
      const closeButton = path.find(el => el instanceof Element && el.classList.contains('js-mega-menu-close'));
      
      if (closeButton) {
        this.close();
      }
    }, { signal: this.#abortController.signal });

    this.addEventListener('keydown', this.#onMegaMenuKeydown, { signal: this.#abortController.signal });

    // Ensure every focusable element inside the submenu (including promo block
    // links/buttons) is removed from the natural Tab order so Tab moves
    // directly from one top-level menuitem to the next. Submenu links remain
    // reachable via the arrow-key navigation handled in #onMegaMenuKeydown.
    this.#makeSubmenuNonTabbable();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Mark every focusable element inside each submenu (mega-menu links, promo
   * block links/buttons, etc.) with tabindex="-1" so Tab skips the submenu
   * entirely and moves directly from one top-level menuitem to the next.
   * Users can still reach sublinks via the arrow-key navigation in
   * #onMegaMenuKeydown, and screen readers can still read them in browse mode.
   */
  #makeSubmenuNonTabbable = () => {
    const submenus = this.querySelectorAll('.menu-list__submenu');
    submenus.forEach((submenu) => {
      const focusables = submenu.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusables.forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
    });
  };

  /**
   * Keyboard navigation for the mega menu. Implements the common disclosure /
   * menubar pattern so assistive-tech users don't have to tab through every
   * sub-link to reach the next top-level group:
   *
   *   - ArrowDown on a top-level menuitem:   open submenu and focus first sublink.
   *   - ArrowDown / ArrowUp inside submenu:  move within the current column.
   *   - ArrowLeft / ArrowRight inside submenu: move between submenu columns.
   *   - Escape inside submenu:               return focus to the parent menuitem.
   *   - Tab inside submenu:                  exits submenu (native behaviour,
   *                                          since sublinks have tabindex="-1",
   *                                          focus lands on the next top-level
   *                                          menuitem).
   *
   * @param {KeyboardEvent} event
   */
  #onMegaMenuKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const { key } = event;
    if (
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'ArrowDown' &&
      key !== 'ArrowUp' &&
      key !== 'Escape'
    ) {
      return;
    }

    const topLevelItem = /** @type {HTMLElement | null} */ (event.target.closest('[ref="menuitem"]'));
    const isOnTopLevel = topLevelItem === event.target;

    // ArrowDown from a top-level menu item moves focus into its submenu.
    if (isOnTopLevel && key === 'ArrowDown') {
      const listItem = topLevelItem.closest('li');
      const submenu = listItem?.querySelector('.menu-list__submenu');
      const firstLink = submenu?.querySelector('a[href]');
      if (firstLink instanceof HTMLElement) {
        event.preventDefault();
        firstLink.focus();
      }
      return;
    }

    const inMega = event.target.closest('.mega-menu__list');
    if (!inMega) return;

    // Escape: return focus to the parent top-level menuitem.
    if (key === 'Escape') {
      const parentListItem = inMega.closest('.menu-list__list-item');
      const parentMenuitem = parentListItem?.querySelector('[ref="menuitem"]');
      if (parentMenuitem instanceof HTMLElement) {
        event.preventDefault();
        parentMenuitem.focus();
      }
      return;
    }

    const column = event.target.closest('li.mega-menu__column');
    if (!column?.parentElement) return;

    // ArrowLeft / ArrowRight: move between submenu columns.
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      const columns = Array.from(
        column.parentElement.querySelectorAll(':scope > li.mega-menu__column')
      );
      const index = columns.indexOf(column);
      if (index === -1) return;
      const delta = key === 'ArrowRight' ? 1 : -1;
      const next = columns[index + delta];
      if (!next) return;
      event.preventDefault();
      const firstLink = next.querySelector('a[href]');
      if (firstLink instanceof HTMLElement) firstLink.focus();
      return;
    }

    // ArrowDown / ArrowUp: move between links within the current column.
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      const links = Array.from(column.querySelectorAll('a[href]'));
      const index = links.indexOf(event.target);
      if (index === -1) return;
      const delta = key === 'ArrowDown' ? 1 : -1;
      const next = links[index + delta];
      if (!(next instanceof HTMLElement)) return;
      event.preventDefault();
      next.focus();
    }
  };

  /**
   * Public method to close the menu immediately.
   * Useful for "Close" buttons inside the mega menu.
   */
  close = () => {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    
    // We call a modified version of the logic or 
    // temporarily override the hover check
    const item = this.#state.activeItem;
    if (!item) return;

    // Reset styles immediately
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    // Reset state
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
